<script setup lang="ts">
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { SmsTopUpInvoiceRecipient } from './AccountPrePaidBalance.access';

    // Own billing details for one invoice (SMS top-up on invoice, R15–R16): off by default; the customer's country is always used,
    // so there is no country field. Mirrors the server's limits (SmsTopUpFacade.NormaliseRecipient); the server stays authoritative.
    const MAX_NAME_OR_LINE_LENGTH = 150;
    const MAX_CITY_LENGTH = 50;
    const MAX_ZIP_LENGTH = 20;
    const MAX_EMAIL_LENGTH = 70;

    type Field = keyof SmsTopUpInvoiceRecipient;
    const REQUIRED: Field[] = ['Company', 'Line1', 'City', 'Zip'];

    const props = defineProps<{ modelValue: SmsTopUpInvoiceRecipient | null; disabled: boolean }>();
    const emit = defineEmits<{ (e: 'update:modelValue', value: SmsTopUpInvoiceRecipient | null): void }>();

    function toggle(on: boolean): void {
        emit('update:modelValue', on ? { Company: '', FirstName: null, LastName: null, Line1: '', Line2: null, City: '', Zip: '', Email: null } : null);
    }

    // Required fields stay strings; a blank optional field is null.
    function set(field: Field, value: unknown): void {
        if (!props.modelValue) return;
        const text = String(value ?? '');
        emit('update:modelValue', { ...props.modelValue, [field]: text === '' && !REQUIRED.includes(field) ? null : text });
    }
</script>

<template>
    <FormCheckable
        class="m-sm-b"
        :label="$Resources.SmsTopUpOtherRecipientToggle ?? ''"
        :switchStyle="SwitchStyle.Standard"
        :checked="props.modelValue != null"
        :disabled="props.disabled"
        @update:modelValue="(v) => toggle(!!v)" />
    <template v-if="props.modelValue">
        <p class="c-light">{{ $Resources.SmsTopUpOtherRecipientHelp }}</p>
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpCompanyLabel"
            :modelValue="props.modelValue.Company"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            :required="true"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('Company', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpFirstNameLabel"
            :modelValue="props.modelValue.FirstName ?? ''"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('FirstName', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpLastNameLabel"
            :modelValue="props.modelValue.LastName ?? ''"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('LastName', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpAddressLine1Label"
            :modelValue="props.modelValue.Line1"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            :required="true"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('Line1', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpAddressLine2Label"
            :modelValue="props.modelValue.Line2 ?? ''"
            :maxlength="MAX_NAME_OR_LINE_LENGTH"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('Line2', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpZipLabel"
            :modelValue="props.modelValue.Zip"
            :maxlength="MAX_ZIP_LENGTH"
            :required="true"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('Zip', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpCityLabel"
            :modelValue="props.modelValue.City"
            :maxlength="MAX_CITY_LENGTH"
            :required="true"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('City', v)" />
        <FormInput
            class="form-group"
            :label="$Resources.SmsTopUpRecipientEmailLabel ?? ''"
            type="email"
            :modelValue="props.modelValue.Email ?? ''"
            :maxlength="MAX_EMAIL_LENGTH"
            :disabled="props.disabled"
            @update:modelValue="(v) => set('Email', v)" />
    </template>
</template>
